/**
 * Send Inquiry Dialog - 新版询盘弹窗
 * 参考 Figma node-id=6562-113543
 * 交互逻辑：多产品胶囊 tab、hover 显示关闭、错误 tab 红点、提交时自动跳转到错误 tab
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import {
  Dialog,
  DialogContent,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@leadsnavi/ui-kit';
import { X, AlertCircle, CircleCheck, ChevronLeft, ChevronRight, Layers, User, XIcon } from 'lucide-react';
import { useHorizontalTabScroll } from '@/components/horizontal-scroll-tabs';
import { ModalComponentProps } from '@/components/moda-lInvoker';
import { SendInquiryDialogProps, SendInquiryProductInfo, BuyerProfile } from './types';
import SendInquiryProductInfoSection from './SendInquiryProductInfoSection';
import BuyerProfileSection from '@/components/inquiry-dialog/BuyerProfileSection';
import { IApiAgentStartReq, IApiUserProfileReq } from '@/services/swagger/easourcing/models';
import { toast } from '@leadsnavi/ui-kit';
import { profileGetPost, savePost } from '@/services/swagger/easourcing/UserProfileService';
import mixpanel from 'mixpanel-browser';
import { Storage } from '@/utils/storage';
import { useDialogVisible } from '@/hooks/useDialogVisible';
import { transFormDataToAgentStartReq } from '@/features/chat-messages/types';
import styles from './index.module.scss';

/** 下划线 Tab 样式：line 效果，仅选中时显示底部 border（依赖 activeContentTab 对应 data-state=active） */
const LINE_TAB_TRIGGER_CLASS =
  'flex w-[152px] items-center gap-2 rounded-none border-none shadow-none bg-transparent px-4 py-3 text-muted-foreground transition-colors rounded-none hover:text-foreground data-[state=active]:shadow-none data-[state=active]:text-foreground flex-none mb-[-2px] border-b-[2px] border-transparent';

/** ProductTab 内产品名称：内容自适应，过长省略号，hover 时 tooltip 展示完整名称 */
function ProductTabName({ name }: { name: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [name]);

  const span = (
    <span ref={ref} className="min-w-0 flex-1 truncate text-sm font-medium block">
      {name}
    </span>
  );

  if (isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{span}</TooltipTrigger>
        <TooltipContent side="top" className="z-[99999] max-w-[280px]">
          {name}
        </TooltipContent>
      </Tooltip>
    );
  }
  return span;
}

const UPDATE_MY_PROFILE_STORAGE_KEY = 'send_inquiry_update_my_profile';
const BUYER_PROFILE_SENT_KEY = 'send_inquiry_buyer_profile_sent';

/** 用于标记是否已展示过首次发送询盘 tooltip 提示，true 表示已展示，false 表示未展示 */
const InquiryTipKey = 'inquiry_tip';

/** 将首次发送询盘 tooltip 提示标记为已展示 */
const markInquiryTipAsOpened = () => {
  Storage.setItem(InquiryTipKey, true);
};

function getUpdateMyProfileFromStorage(): boolean {
  try {
    const v = localStorage.getItem(UPDATE_MY_PROFILE_STORAGE_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
}

function hasSentInquiryBefore(): boolean {
  try {
    return localStorage.getItem(BUYER_PROFILE_SENT_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * 根据 supplier 的 defaultValues 构建产品信息默认值。
 * - 若 orderQuantity 缺失，尝试通过 maxBudget / preferredUnitPrice 推算。
 * - 若 maxBudget 缺失，尝试通过 orderQuantity * preferredUnitPrice 推算。
 */
function buildDefaultProductInfo(dv: Partial<SendInquiryProductInfo> | undefined): SendInquiryProductInfo {
  const preferredUnitPrice = parseFloat(dv?.preferredUnitPrice ?? '');

  let orderQuantity = dv?.orderQuantity || '';
  if (!orderQuantity && dv?.maxBudget) {
    const maxBudget = parseFloat(dv.maxBudget);
    if (!isNaN(maxBudget) && !isNaN(preferredUnitPrice) && preferredUnitPrice > 0) {
      orderQuantity = String(Math.round(maxBudget / preferredUnitPrice));
    }
  }

  let maxBudget = dv?.maxBudget || '';
  if (!maxBudget && orderQuantity) {
    const qty = parseFloat(orderQuantity);
    if (!isNaN(qty) && !isNaN(preferredUnitPrice)) {
      maxBudget = String(Math.round(qty * preferredUnitPrice));
    }
  }

  return {
    productName: dv?.productName || '',
    orderQuantity,
    unitCode: dv?.unitCode || 'Piece(s)',
    tradeTerms: dv?.tradeTerms || 'FOB',
    preferredUnitPrice: dv?.preferredUnitPrice || '',
    currency: dv?.currency || 'USD',
    maxBudget,
    specifications: dv?.specifications || '',
    modelNo: dv?.modelNo || '',
    details: dv?.details || '',
    shippingMethod: dv?.shippingMethod || 'Sea Freight',
    paymentMethod: dv?.paymentMethod || 'T/T',
    leadTime: dv?.leadTime || '',
    validTo: dv?.validTo || '',
    validToDays: dv?.validToDays || '30 days',
  };
}

const SendInquiryDialog: React.FC<SendInquiryDialogProps & ModalComponentProps> = ({
  open: openProp,
  visible,
  // onOpenChange,
  onCancel,
  suppliers: initialSuppliers,
  sendInquiryParams,
  initialBuyerProfile,
  onSubmit,
  onSuccess: _onSuccess,
}) => {
  const open = openProp ?? visible ?? false;
  const [remainingSuppliers, setRemainingSuppliers] = useState(initialSuppliers);
  const [activeProductTab, setActiveProductTab] = useState<string>(initialSuppliers[0]?.id || '');
  const [activeContentTab, setActiveContentTab] = useState<'product' | 'buyer'>('product');
  const [productInfoMap, setProductInfoMap] = useState<Record<string, SendInquiryProductInfo>>({});
  const [buyerProfile, setBuyerProfile] = useState<BuyerProfile>({
    id: '',
    name: '',
    location: '',
    companyName: '',
    industry: '',
    companyIntroduction: '',
    updateMyProfile: getUpdateMyProfileFromStorage(),
  });
  const [isProfileCollapsed, setIsProfileCollapsed] = useState(hasSentInquiryBefore());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  /** 首次发送询盘时展示的引导 tooltip，读取 Storage 判断是否展示过 */
  const [openInquiryTip, setOpenInquiryTip] = useState(() => {
    const shouldShow = Storage.getItem<boolean>(InquiryTipKey) !== true;
    markInquiryTipAsOpened();
    return shouldShow;
  });
  const { scrollRef: tabScrollRef, navigate: navigateProductTab } = useHorizontalTabScroll(
    remainingSuppliers,
    activeProductTab,
    setActiveProductTab
  );

  const productInfo = productInfoMap[activeProductTab] || buildDefaultProductInfo(undefined);

  useEffect(() => {
    try {
      localStorage.setItem(UPDATE_MY_PROFILE_STORAGE_KEY, String(buyerProfile.updateMyProfile));
    } catch {
      /* ignore */
    }
  }, [buyerProfile.updateMyProfile]);

  useEffect(() => {
    if (open) {
      setRemainingSuppliers(initialSuppliers);
      setIsProfileCollapsed(hasSentInquiryBefore());
      setActiveProductTab(initialSuppliers[0]?.id || '');
      const initialMap: Record<string, SendInquiryProductInfo> = {};
      initialSuppliers.forEach(s => {
        initialMap[s.id] = buildDefaultProductInfo(s.defaultValues);
      });
      setProductInfoMap(initialMap);
    }
  }, [open, initialSuppliers]);

  useEffect(() => {
    if (remainingSuppliers.length > 0 && !remainingSuppliers.find(s => s.id === activeProductTab)) {
      setActiveProductTab(remainingSuppliers[0].id);
    }
  }, [remainingSuppliers, activeProductTab]);

  useEffect(() => {
    if (open && activeProductTab) {
      const s = remainingSuppliers.find(x => x.id === activeProductTab);
      if (s?.defaultValues && !productInfoMap[activeProductTab]) {
        setProductInfoMap(prev => ({
          ...prev,
          [activeProductTab]: buildDefaultProductInfo(s.defaultValues),
        }));
      }
    }
  }, [open, activeProductTab, remainingSuppliers, productInfoMap]);

  const fetchUserProfile = useCallback(async () => {
    try {
      const res = await profileGetPost();
      if (res?.data) {
        const p = res.data;
        setBuyerProfile({
          id: p.id || '',
          name: p.name || '',
          location: p.location || '',
          companyName: p.companyName || '',
          industry: p.industry || '',
          companyIntroduction: p.companyIntroduction || '',
          updateMyProfile: getUpdateMyProfileFromStorage(),
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (initialBuyerProfile) {
      // 入参有买家信息时，直接合并覆盖，保留 updateMyProfile 等本地状态
      setBuyerProfile(prev => ({
        ...prev,
        ...initialBuyerProfile,
      }));
    } else {
      fetchUserProfile();
    }
  }, [open, initialBuyerProfile, fetchUserProfile]);

  const hasProductInfoErrors = useCallback(() => {
    return (
      !productInfo.productName.trim() ||
      !productInfo.orderQuantity.trim() ||
      !productInfo.maxBudget.trim() ||
      !productInfo.tradeTerms.trim() ||
      !productInfo.preferredUnitPrice.trim() ||
      !productInfo.details.trim() ||
      !!errors.preferredUnitPrice ||
      !!errors.maxBudget ||
      !!errors.leadTime
    );
  }, [productInfo, errors.preferredUnitPrice, errors.maxBudget, errors.leadTime]);

  const hasBuyerProfileErrors = useCallback(() => {
    return !buyerProfile.name.trim() || !buyerProfile.location.trim() || !buyerProfile.companyName.trim();
  }, [buyerProfile]);

  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    if (!productInfo.productName.trim()) newErrors.productName = 'Product Name is required';
    if (!productInfo.orderQuantity.trim()) newErrors.orderQuantity = 'Order Quantity is required';
    if (!productInfo.tradeTerms.trim()) newErrors.tradeTerms = 'Trade Terms is required';
    if (!productInfo.preferredUnitPrice.trim()) {
      newErrors.preferredUnitPrice = 'Preferred Unit Price is required';
    } else if (!/^\d+$/.test(productInfo.preferredUnitPrice)) {
      newErrors.preferredUnitPrice = 'Please enter a valid whole number.';
    }
    if (!productInfo.maxBudget.trim()) {
      newErrors.maxBudget = 'Max Budget is required';
    } else if (!/^\d+$/.test(productInfo.maxBudget)) {
      newErrors.maxBudget = 'Please enter a valid whole number.';
    } else if (parseInt(productInfo.maxBudget, 10) < 1) {
      newErrors.maxBudget = 'Max Budget must be at least 1.';
    }
    if (productInfo.leadTime.trim() && !/^\d+$/.test(productInfo.leadTime)) {
      newErrors.leadTime = 'Please enter a valid numeric format.';
    }
    if (!productInfo.details.trim()) newErrors.details = 'Details is required';
    if (!buyerProfile.name.trim()) newErrors.name = 'Name is required';
    if (!buyerProfile.location.trim()) newErrors.location = 'Location is required';
    if (!buyerProfile.companyName.trim()) newErrors.companyName = 'Company Name is required';
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  }, [productInfo, buyerProfile]);

  useEffect(() => {
    const next = { ...errors };
    let changed = false;
    if (productInfo.productName.trim() && next.productName) {
      delete next.productName;
      changed = true;
    }
    if (productInfo.orderQuantity.trim() && next.orderQuantity) {
      delete next.orderQuantity;
      changed = true;
    }
    if (
      productInfo.maxBudget.trim() &&
      /^\d+$/.test(productInfo.maxBudget) &&
      parseInt(productInfo.maxBudget, 10) >= 1 &&
      next.maxBudget
    ) {
      delete next.maxBudget;
      changed = true;
    }
    if (productInfo.tradeTerms.trim() && next.tradeTerms) {
      delete next.tradeTerms;
      changed = true;
    }
    if (
      productInfo.preferredUnitPrice.trim() &&
      /^\d+$/.test(productInfo.preferredUnitPrice) &&
      next.preferredUnitPrice
    ) {
      delete next.preferredUnitPrice;
      changed = true;
    }
    if (productInfo.leadTime.trim() && /^\d+$/.test(productInfo.leadTime) && next.leadTime) {
      delete next.leadTime;
      changed = true;
    }
    if (productInfo.details.trim() && next.details) {
      delete next.details;
      changed = true;
    }
    if (buyerProfile.name.trim() && next.name) {
      delete next.name;
      changed = true;
    }
    if (buyerProfile.location.trim() && next.location) {
      delete next.location;
      changed = true;
    }
    if (buyerProfile.companyName.trim() && next.companyName) {
      delete next.companyName;
      changed = true;
    }
    if (changed) setErrors(next);
  }, [
    productInfo.productName,
    productInfo.orderQuantity,
    productInfo.maxBudget,
    productInfo.tradeTerms,
    productInfo.preferredUnitPrice,
    productInfo.leadTime,
    productInfo.details,
    buyerProfile.name,
    buyerProfile.location,
    buyerProfile.companyName,
    errors,
  ]);

  const buildInquiryRequest = useCallback((): IApiAgentStartReq => {
    const s = remainingSuppliers.find(x => x.id === activeProductTab);
    if (!s) throw new Error('No supplier selected');
    return transFormDataToAgentStartReq(productInfo, buyerProfile, s);
  }, [remainingSuppliers, activeProductTab, productInfo, buyerProfile]);

  const handleClose = useCallback(() => {
    onCancel?.();
    // onOpenChange?.(false);
  }, [onCancel]);

  const getNextActiveTab = useCallback((removedId: string, currentList: typeof remainingSuppliers): string | null => {
    const currentIndex = currentList.findIndex(s => s.id === removedId);
    if (currentIndex === -1) return null;

    const nextList = currentList.filter(s => s.id !== removedId);
    if (nextList.length === 0) return null;

    // 优先选择下一个tab，如果没有则选择前一个
    if (currentIndex < currentList.length - 1) {
      // 有下一个，返回下一个
      return currentList[currentIndex + 1].id;
    } else if (currentIndex > 0) {
      // 没有下一个但有前一个，返回前一个
      return currentList[currentIndex - 1].id;
    }
    // 兜底：返回过滤后的第一个
    return nextList[0]?.id || null;
  }, []);

  const handleSubmit = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      // 使用最新的 errors 来判断哪个 tab 有错误
      const hasProductErrors =
        !productInfo.productName.trim() ||
        !productInfo.orderQuantity.trim() ||
        !productInfo.maxBudget.trim() ||
        !productInfo.tradeTerms.trim() ||
        !productInfo.preferredUnitPrice.trim() ||
        !productInfo.details.trim() ||
        !!validation.errors.preferredUnitPrice ||
        !!validation.errors.maxBudget ||
        !!validation.errors.leadTime;

      const hasBuyerErrors =
        !buyerProfile.name.trim() || !buyerProfile.location.trim() || !buyerProfile.companyName.trim();

      if (hasBuyerErrors && isProfileCollapsed) setIsProfileCollapsed(false);
      setActiveContentTab(hasProductErrors ? 'product' : 'buyer');
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // 保存买家 profile（如果勾选了 updateMyProfile）
      if (buyerProfile.updateMyProfile) {
        const params: IApiUserProfileReq = {
          name: buyerProfile.name,
          companyName: buyerProfile.companyName,
          industry: buyerProfile.industry,
          companyIntroduction: buyerProfile.companyIntroduction,
          location: buyerProfile.location,
        };
        if (buyerProfile.id) params.id = buyerProfile.id;
        await savePost(params);
      }

      // 构建合法的 IApiAgentStartReq 参数，直接调用外部 onSubmit 回调，同时透传买家信息
      const inquiryData = buildInquiryRequest();
      await onSubmit?.({ ...sendInquiryParams, ...inquiryData }, buyerProfile);

      try {
        localStorage.setItem(BUYER_PROFILE_SENT_KEY, 'true');
      } catch {
        /* ignore */
      }

      // 提交成功后移除当前 supplier tab，切换到下一个
      const next = remainingSuppliers.filter(x => x.id !== activeProductTab);
      setRemainingSuppliers(next);
      setProductInfoMap(prev => {
        const m = { ...prev };
        delete m[activeProductTab];
        return m;
      });
      const nextTabId = getNextActiveTab(activeProductTab, remainingSuppliers);
      if (nextTabId) setActiveProductTab(nextTabId);
      else handleClose();
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [
    validateForm,
    productInfo.productName,
    productInfo.orderQuantity,
    productInfo.maxBudget,
    productInfo.tradeTerms,
    productInfo.preferredUnitPrice,
    productInfo.details,
    buyerProfile,
    isProfileCollapsed,
    buildInquiryRequest,
    onSubmit,
    sendInquiryParams,
    remainingSuppliers,
    getNextActiveTab,
    activeProductTab,
    handleClose,
  ]);

  const removeProductTab = useCallback(
    (id: string) => {
      const next = remainingSuppliers.filter(s => s.id !== id);
      setRemainingSuppliers(next);
      setProductInfoMap(prev => {
        const m = { ...prev };
        delete m[id];
        return m;
      });
      if (activeProductTab === id) {
        const nextTabId = getNextActiveTab(id, remainingSuppliers);
        if (nextTabId) setActiveProductTab(nextTabId);
        else handleClose();
      }
    },
    [activeProductTab, remainingSuppliers, handleClose, getNextActiveTab]
  );
  const hasMissingRequired = hasProductInfoErrors() || hasBuyerProfileErrors();
  const productTabHasError = hasProductInfoErrors();
  const buyerTabHasError = hasBuyerProfileErrors();

  const buyerProfileForSection = {
    id: buyerProfile.id,
    name: buyerProfile.name,
    location: buyerProfile.location,
    companyName: buyerProfile.companyName,
    industry: buyerProfile.industry,
    companyIntroduction: buyerProfile.companyIntroduction,
    updateMyProfile: buyerProfile.updateMyProfile,
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn(
          'max-w-4xl h-[722px] max-h-[90vh] overflow-visible flex flex-col p-0 border-none bg-white rounded-[10px] shadow-lg'
        )}
        showCloseButton={false}
      >
        {/* <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="absolute -top-10 -right-10 z-[9999] h-8 w-8 p-0 hover:bg-transparent"
        >
          <X className="h-4 w-4 text-white" />
        </Button> */}

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header: Send Inquiry + product tabs + arrows */}
          <div className="flex-shrink-0 border-b-0 border-[#E5E5E5] ">
            <div className="flex items-center justify-between gap-4 p-4 border-b border-[#E5E5E5]">
              <h2 className="text-lg font-semibold text-[#171717] ">Send Inquiry</h2>
              <div className="flex justify-end gap-1">
                {remainingSuppliers.length > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => navigateProductTab('left')}
                      className="p-1 rounded hover:bg-[#F5F5F5] border cursor-pointer"
                    >
                      <ChevronLeft size={20} strokeWidth={1.5} className="text-[#737373]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateProductTab('right')}
                      className="p-1 rounded hover:bg-[#F5F5F5] border cursor-pointer"
                    >
                      <ChevronRight size={20} strokeWidth={1.5} className="text-[#737373]" />
                    </button>
                    <span className="bg-[#E5E5E5] h-4 w-[1px] ml-4"></span>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={handleClose}
                  className="hover:bg-[#F5F5F5] border-none cursor:pointter bg-white h-8 w-8 p-0 shadow-none"
                >
                  <X className="h-4 w-4 text-[#525252]" />
                </Button>
              </div>
            </div>

            {/* Product capsule tabs - 定宽 160px，hover 显示关闭 icon，名称过长省略号 + tooltip */}
            {initialSuppliers.length > 1 && (
              <div
                ref={tabScrollRef}
                className="flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-[#E5E5E5] p-4"
                style={{ scrollbarWidth: 'thin' }}
              >
                {remainingSuppliers.map(s => {
                  const isActive = s.id === activeProductTab;
                  const isHovered = hoveredTabId === s.id;
                  const showClose = (isHovered || isActive) && remainingSuppliers.length > 1;
                  const productName =
                    productInfoMap[s.id]?.productName || s.defaultValues?.productName || s.name || 'Product';
                  return (
                    <div
                      key={s.id}
                      data-scroll-id={s.id}
                      className={cn(
                        'flex items-center gap-2 rounded-[38px] px-4 py-2 w-[160px] min-w-[160px] cursor-pointer transition-colors',
                        isActive ? 'bg-[#171717] text-white' : 'bg-white border border-[#E5E5E5] text-[#525252]'
                      )}
                      onClick={() => setActiveProductTab(s.id)}
                      onMouseEnter={() => setHoveredTabId(s.id)}
                      onMouseLeave={() => setHoveredTabId(null)}
                    >
                      <ProductTabName name={productName} />
                      {showClose && (
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            removeProductTab(s.id);
                          }}
                          className="p-0.5 rounded-full hover:bg-white/20 shrink-0"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Info / Buyer Profile tabs - 内容区域适配屏幕，超出显示滚动条 */}
          <div className="flex-1 min-h-0 flex flex-col ">
            <Tabs
              value={activeContentTab}
              onValueChange={v => setActiveContentTab(v as 'product' | 'buyer')}
              className="flex-1 min-h-0 flex flex-col"
            >
              <TabsList className="pl-4 w-full justify-start h-12 rounded-none bg-transparent border-b-1 border-[#D4D4D4] py-0">
                <TabsTrigger
                  style={{ borderBottom: activeContentTab === 'product' ? '2px solid #171717' : '' }}
                  value="product"
                  className={cn(LINE_TAB_TRIGGER_CLASS)}
                >
                  <Layers size={16} />
                  <span>Product Info</span>
                  {productTabHasError && (
                    <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
                  )}
                </TabsTrigger>
                <TabsTrigger
                  style={{ borderBottom: activeContentTab === 'buyer' ? '2px solid #171717' : '' }}
                  value="buyer"
                  className={LINE_TAB_TRIGGER_CLASS}
                >
                  <User size={16} />
                  <span>Buyer Profile</span>
                  {buyerTabHasError && <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />}
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 min-h-0 overflow-y-auto">
                <TabsContent value="product" className="mt-0">
                  <SendInquiryProductInfoSection
                    key={activeProductTab}
                    productInfo={productInfo}
                    onChange={info => setProductInfoMap(prev => ({ ...prev, [activeProductTab]: info }))}
                    errors={errors}
                  />
                </TabsContent>
                <TabsContent value="buyer" className="mt-0">
                  <BuyerProfileSection
                    buyerProfile={buyerProfileForSection}
                    onChange={setBuyerProfile}
                    isCollapsed={isProfileCollapsed}
                    onToggleCollapse={setIsProfileCollapsed}
                    errors={errors}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-[#E5E5E5] px-4 py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {hasMissingRequired ? (
                <>
                  <AlertCircle className="h-4 w-4 text-[#F97316]" />
                  <span className="text-sm font-normal text-[#F97316]">Essentials Missing</span>
                </>
              ) : (
                <>
                  <CircleCheck className="h-4 w-4 text-[#10B981]" />
                  <span className="text-sm font-normal text-[#10B981]">Ready to Send</span>
                </>
              )}
            </div>
            <Tooltip open={openInquiryTip}>
              <TooltipTrigger asChild>
                <Button
                  id="inquiry_form_submit_click"
                  onClick={() => {
                    mixpanel.track('inquiry_form_submit_click');
                    handleSubmit();
                  }}
                  disabled={loading}
                  loading={loading}
                  className={` rounded-lg${hasMissingRequired ? ' opacity-50 cursor-not-allowed' : ''}`}
                >
                  Save
                </Button>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-[392px] p-1 bg-[#FEF3C7] py-2 px-4 [&>svg]:hidden"
                side="top"
                sideOffset={5}
                arrowClassName={styles.tooltipArrow}
                align="end"
              >
                <div className="flex justify-between items-center">
                  <div className="text-[#09090B] font-medium [font-family:Geist] text-base leading-[150%]">
                    Congratulations on your first search!
                  </div>
                  <XIcon
                    color="#525252"
                    size={16}
                    className="cursor-pointer"
                    onClick={() => {
                      setOpenInquiryTip(false);
                    }}
                  />
                </div>
                <p className="break-words overflow-auto max-h-[400px] text-[#262626] text-xs mt-1 self-stretch font-normal leading-[150%]">
                  Easesourcing offers you a FREE email-sending account. Submit an inquiry now to receive multiple quotes
                  instantly.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SendInquiryDialogPortal: React.FC<SendInquiryDialogProps & ModalComponentProps> = props => {
  const { open: openProp, visible } = props;
  const open = openProp ?? visible ?? false;

  const openV2 = useDialogVisible(open);
  if (!openV2) {
    return null;
  }
  return <SendInquiryDialog {...props} />;
};

export default SendInquiryDialog;
export * from './types';
